import React, { useState } from 'react';
import {
  Text, HStack, Badge, Icon, Avatar, Collapse, Box, Button, Flex,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiCalendar } from 'react-icons/fi';
import BugTicketDetails from './BugTicketDetails';
import useAnalyticsAPI from '../hooks/useAnalyticsAPI';

export default function ModuleIssuesTable({ issues }) {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Fixed at 10 rows per page
  const [loadingAI, setLoadingAI] = useState(new Set()); // Track which rows are loading AI data
  
  const { makeRequest } = useAnalyticsAPI();

  // Calculate pagination
  const totalPages = Math.ceil(issues.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageIssues = issues.slice(startIndex, endIndex);
  
  const hasNext = currentPage < totalPages;
  const hasPrev = currentPage > 1;

  const toggleRowExpansion = async (ticketId) => {
    const newExpandedRows = new Set(expandedRows);
    const isExpanding = !newExpandedRows.has(ticketId);
    
    if (isExpanding) {
      newExpandedRows.add(ticketId);
      setExpandedRows(newExpandedRows);
      
      // Fetch AI analysis data when expanding
      await fetchAIAnalysis(ticketId);
    } else {
      newExpandedRows.delete(ticketId);
      setExpandedRows(newExpandedRows);
    }
  };

  const fetchAIAnalysis = async (issueKey) => {
    try {
      setLoadingAI(prev => new Set(prev).add(issueKey));
      
      // Fetch AI analysis from the specified endpoint
      const result = await makeRequest(`/api/bugs/analytics/ai-analysis/${issueKey}`, {});
      
      if (result.status === 'success') {
        console.log(`AI analysis loaded for ${issueKey}:`, result.data);
        // The BugTicketDetails component will handle the data display
      } else {
        console.warn(`No AI analysis data for ${issueKey}`);
      }
    } catch (error) {
      console.error(`Failed to fetch AI analysis for ${issueKey}:`, error);
    } finally {
      setLoadingAI(prev => {
        const newSet = new Set(prev);
        newSet.delete(issueKey);
        return newSet;
      });
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Close all expanded rows when changing pages
    setExpandedRows(new Set());
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'yellow';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'done': return 'green';
      case 'in progress': return 'blue';
      case 'qa': return 'purple';
      case 'todo': return 'gray';
      default: return 'gray';
    }
  };

  const getClientImpactColor = (impact) => {
    switch (impact?.toLowerCase()) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  if (issues.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500">No issues found for this module.</Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Table */}
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Issue Key</Th>
              <Th>Severity</Th>
              <Th>Cluster</Th>
              <Th>Summary</Th>
              <Th>Client Impact</Th>
              <Th>Status</Th>
              <Th>Assignee</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentPageIssues.map((ticket) => (
              <React.Fragment key={ticket.issueKey}>
                <Tr 
                  cursor="pointer" 
                  onClick={() => toggleRowExpansion(ticket.issueKey)} 
                  _hover={{ bg: "gray.50" }}
                >
                  <Td>
                    <Text fontWeight="bold" color="blue.600">
                      {ticket.issueKey}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={getSeverityColor(ticket.severity)} size="sm">
                      {ticket.severity}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="purple" variant="outline" size="sm">
                      {ticket.cluster}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm" noOfLines={2}>
                      {ticket.summary}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={getClientImpactColor(ticket.clientImpact)} size="sm">
                      {ticket.clientImpact}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={getStatusColor(ticket.status)} size="sm">
                      {ticket.status}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Avatar size="xs" name={ticket.assignee} bg="blue.500" color="white">
                        {getInitials(ticket.assignee)}
                      </Avatar>
                      <Text fontSize="sm">{ticket.assignee}</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Icon as={FiCalendar} size="xs" color="gray.500" />
                      <Text fontSize="sm" color="gray.600">
                        {formatDate(ticket.createdAt)}
                      </Text>
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      {loadingAI.has(ticket.issueKey) && (
                        <Spinner size="xs" color="blue.500" />
                      )}
                      <Icon 
                        as={expandedRows.has(ticket.issueKey) ? FiChevronDown : FiChevronRight} 
                        color="gray.500" 
                      />
                    </HStack>
                  </Td>
                </Tr>
                <Tr>
                  <Td colSpan={9} p={0}>
                    <Collapse in={expandedRows.has(ticket.issueKey)} animateOpacity>
                      <Box p={4} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
                        {loadingAI.has(ticket.issueKey) ? (
                          <HStack spacing={3} justify="center" py={4}>
                            <Spinner size="sm" color="blue.500" />
                            <Text fontSize="sm" color="gray.600">
                              Loading AI analysis...
                            </Text>
                          </HStack>
                        ) : (
                          <BugTicketDetails issueKey={ticket.issueKey} ticket={ticket} />
                        )}
                      </Box>
                    </Collapse>
                  </Td>
                </Tr>
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Flex justify="center" align="center" mt={6} spacing={4}>
          <Button
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            isDisabled={!hasPrev}
            variant="outline"
          >
            Previous
          </Button>
          
          <HStack spacing={2} mx={4}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                size="sm"
                variant={currentPage === page ? "solid" : "outline"}
                colorScheme={currentPage === page ? "blue" : "gray"}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Button>
            ))}
          </HStack>
          
          <Button
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            isDisabled={!hasNext}
            variant="outline"
          >
            Next
          </Button>
        </Flex>
      )}

      {/* Page Info */}
      <Box textAlign="center" mt={4}>
        <Text fontSize="sm" color="gray.500">
          Showing {startIndex + 1}-{Math.min(endIndex, issues.length)} of {issues.length} issues
          {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
        </Text>
      </Box>
    </Box>
  );
}
